import { DateFormat } from "common";
import { Dayjs } from "dayjs";
import { LineItemType, Partner, PurchaseStatus } from "types";
import { api } from "../api/api";
import { config } from "../config";
import { PartnerCommissionsFilter } from "../stores/reports/partnerCommissionsFilterStore";

export interface PartnersReportData extends Partner {
    balanceDue: number;
    partnerCommissionTerm: string;
    partnerDiscountTerm: string;
}

export interface PartnerCommissionsReportParams {
    sellerId: ID;
    from: Dayjs;
    to: Dayjs;
    filters: PartnerCommissionsFilter;
}

export const getPartnerCommissionsReport = async (params: PartnerCommissionsReportParams) => {
    const { sellerId } = params;
    const index = config.elasticsearch.indexes.purchase;
    const { data } = await api.post(`/elasticsearch/${index}/_search?seller=${sellerId}`, getPayload(params));
    return getReport(data);
};

export interface AggregatedPartnerData {
    commission: number;
    payment: number;
    deposit: number;
}

export interface Dues {
    [key: ID]: AggregatedPartnerData;
}

const aggregationMappings = [
    {
        aggName: "commissions",
        fieldName: "commission" as keyof AggregatedPartnerData,
        valuePath: ["commission", "value"],
    },
    {
        aggName: "payments",
        fieldName: "payment" as keyof AggregatedPartnerData,
        valuePath: ["payment", "value"],
    },
    {
        aggName: "deposits",
        fieldName: "deposit" as keyof AggregatedPartnerData,
        valuePath: ["deposit_lineItems", "deposit_lineItem", "deposit", "value"],
        isNegative: true,
    },
];

/**
 * Process the response from the Elasticsearch call. This will maps partner ID with aggregated data.
 */
const getReport = (data: any): Dues => {
    const dues: Dues = {};

    processAggregations(dues, data, aggregationMappings);

    return dues;
};

const processAggregations = (
    dues: Dues,
    data: any,
    mappings: Array<{
        aggName: string;
        fieldName: keyof AggregatedPartnerData;
        valuePath: string[];
        isNegative?: boolean;
    }>,
) => {
    for (const mapping of mappings) {
        const buckets = data.aggregations?.[mapping.aggName]?.buckets || [];
        for (const agg of buckets) {
            const { key } = agg;
            if (!dues[key]) {
                dues[key] = getInitalAggregatedPartnerData();
            }

            // eslint-disable-next-line unicorn/no-array-reduce
            const value = mapping.valuePath.reduce((object, path) => object?.[path], agg) || 0;
            dues[key][mapping.fieldName] = mapping.isNegative ? -value : value;
        }
    }
};

const getInitalAggregatedPartnerData = (): AggregatedPartnerData => {
    return {
        commission: 0,
        payment: 0,
        deposit: 0,
    };
};

const getPayload = ({ sellerId, from, to }: PartnerCommissionsReportParams) => {
    const start = from.format(DateFormat.DATE_ISO);
    const end = to.format(DateFormat.DATE_ISO);
    const dateRange = { gte: start, lte: end };
    const rangeFilter: any = {
        nested: {
            path: "items",
            query: { range: { "items.arrivalDate": dateRange } },
        },
    };

    const affiliateCommissionFilter: any = {
        bool: {
            should: [
                {
                    range: {
                        affiliateCommission: {
                            gte: 0,
                        },
                    },
                },
                {
                    bool: {
                        must_not: [
                            {
                                exists: {
                                    field: "affiliateCommission",
                                },
                            },
                        ],
                    },
                },
            ],
        },
    };

    const existsFilter = {
        bool: {
            should: [
                {
                    exists: {
                        field: "partner",
                    },
                },
            ],
        },
    };

    const must: any = [
        rangeFilter,
        { term: { "seller.id": sellerId } },
        { term: { status: PurchaseStatus.COMMITTED } },
        existsFilter,
        affiliateCommissionFilter,
    ];

    return {
        size: 0,
        query: {
            bool: { must },
        },
        aggs: {
            // Commissions
            ...getTermsAggregation("partner.id", "commissions", "affiliateCommission", "commission"),

            // Payments
            ...getTermsAggregation("partner.id", "payments", "affiliatePayment", "payment"),

            // Deposits
            ...getNestedAggregation("partner.id", "deposits", LineItemType.AFFILIATE_DEPOSIT, "deposit"),
        },
    };
};

const getTermsAggregation = (idField: string, aggName: string, sumField: string, sumAggName: string) => {
    return {
        [aggName]: {
            terms: {
                field: idField,
                size: 9_999_999,
            },
            aggs: {
                [sumAggName]: {
                    sum: {
                        field: sumField,
                    },
                },
            },
        },
    };
};

const getNestedAggregation = (idField: string, aggName: string, lineItemType: LineItemType, sumAggName: string) => {
    return {
        [aggName]: {
            terms: {
                field: idField,
                size: 9_999_999,
            },
            aggs: {
                deposit_lineItems: {
                    nested: {
                        path: "lineItems",
                    },
                    aggs: {
                        deposit_lineItem: {
                            filter: {
                                term: {
                                    "lineItems.type": lineItemType,
                                },
                            },
                            aggs: {
                                [sumAggName]: {
                                    sum: {
                                        field: "lineItems.amount",
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
    };
};
